import type { Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { offsetPath } from './gables';
import { octahedronSolid, orientedFace } from './solids';

/**
 * STYLE.md §8: light gable edges and a light finial dot at the apex. The coping is a
 * closed band 0.15 m wide straddling the gable's top outline, standing slightly proud
 * of the facade and of the roof so it never shares a plane with either.
 */
export const COPING = {
  width: 0.15,
  lift: 0.04,
  proud: 0.02,
  back: 0.12,
  finialRadius: 0.2,
  finialSink: 0.1,
  sideClearance: 0.005,
} as const;

function at(p: Vec3, x: number): Vec3 {
  return [x, p[1], p[2]];
}

function segmentOutward(a: Vec3, b: Vec3): Vec3 {
  return [0, -(b[2] - a[2]), b[1] - a[1]];
}

function emitSegment(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  k: number,
  bands: { readonly outer: readonly Vec3[]; readonly inner: readonly Vec3[] },
  xs: readonly [number, number]
): void {
  const [x0, x1] = xs;
  const o0 = bands.outer[k]!;
  const o1 = bands.outer[k + 1]!;
  const i0 = bands.inner[k]!;
  const i1 = bands.inner[k + 1]!;
  const out = segmentOutward(o0, o1);
  const inward: Vec3 = [0, -out[1], -out[2]];
  orientedFace(builder, style, frame, [at(i0, x0), at(i1, x0), at(o1, x0), at(o0, x0)], [-1, 0, 0]);
  orientedFace(builder, style, frame, [at(i0, x1), at(i1, x1), at(o1, x1), at(o0, x1)], [1, 0, 0]);
  orientedFace(builder, style, frame, [at(o0, x0), at(o1, x0), at(o1, x1), at(o0, x1)], out);
  orientedFace(builder, style, frame, [at(i0, x0), at(i1, x0), at(i1, x1), at(i0, x1)], inward);
}

function emitCap(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  ends: { readonly outer: Vec3; readonly inner: Vec3; readonly away: Vec3 },
  xs: readonly [number, number]
): void {
  const [x0, x1] = xs;
  const { outer, inner, away } = ends;
  const pts = [at(inner, x0), at(outer, x0), at(outer, x1), at(inner, x1)];
  orientedFace(builder, style, frame, pts, away);
}

/** A band along a gable's edge: how wide, how far above the edge its top runs, how far proud of the face. */
export interface TrimSpec {
  readonly width: number;
  readonly lift: number;
  readonly proud: number;
}

function squeezeToWidth(points: readonly Vec3[], halfWidth: number): Vec3[] {
  const reach = Math.max(...points.map((p) => Math.abs(p[2])));
  const scale = Math.min(1, (halfWidth - COPING.sideClearance) / reach);
  return points.map((p): Vec3 => [p[0], p[1], p[2] * scale]);
}

/**
 * Coping band along an open gable path in the house frame, from x = -proud to xBack,
 * squeezed across so it stays between the party-wall planes at +-halfWidth. A timber gable's
 * barge boards are the same band, cut to their own `spec`.
 */
export function buildCoping(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  path: readonly Vec3[],
  bounds: { readonly xBack: number; readonly halfWidth: number },
  spec: TrimSpec = COPING
): void {
  const outer = offsetPath(path, spec.lift);
  const inner = offsetPath(path, spec.lift - spec.width);
  const squeezed = squeezeToWidth([...outer, ...inner], bounds.halfWidth);
  const bands = { outer: squeezed.slice(0, path.length), inner: squeezed.slice(path.length) };
  const xs = [-spec.proud, bounds.xBack] as const;
  for (let k = 0; k < path.length - 1; k++) emitSegment(builder, style, frame, k, bands, xs);
  const last = path.length - 1;
  const first = path[0]!;
  const second = path[1]!;
  const end = path[last]!;
  const before = path[last - 1]!;
  emitCap(builder, style, frame, {
    outer: bands.outer[0]!,
    inner: bands.inner[0]!,
    away: [0, first[1] - second[1], first[2] - second[2]],
  }, xs);
  emitCap(builder, style, frame, {
    outer: bands.outer[last]!,
    inner: bands.inner[last]!,
    away: [0, end[1] - before[1], end[2] - before[2]],
  }, xs);
}

/** Small light knob sitting on the coping at the gable apex. */
export function buildFinial(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  apex: Vec3,
  x: number
): void {
  const r = COPING.finialRadius;
  octahedronSolid(builder, style, frame, [x, apex[1] + COPING.lift + r - COPING.finialSink, apex[2]], r);
}
